import { useState, useEffect } from "react";
import { db } from "../../backend/firebaseConfig";
import { collection, doc, getDocs, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import useUserRole from "../../hooks/useUserRole";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BarChart, Bar, PieChart, Pie, Cell, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { servicesList } from "../../components/UI/organisms/list/ServicesList";


// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          Grid,
          CircularProgress,
          TableContainer,
          Table,
          TableHead,
          TableRow,
          TableCell,
          Paper,
          TableBody, 
          TableFooter,
          Pagination,
          Dialog,
          DialogTitle,
          DialogContent,
          DialogActions,
          IconButton,
          Snackbar,
          Divider,
          Alert as MuiAlert,
          useMediaQuery
} from "@mui/material"
import { useTheme } from "@mui/material/styles";

// Import Organisms
import Sidebar from "../../components/UI/organisms/sidebar/Sidebar"

// Import Molecules
import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn"

import SplashScreen from "../../components/UI/atoms/splash/SplashScreen"

// Import Functions
import { fetchUserDetails } from "../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../components/functions/redirectIfNotLoggedIn";
import ButtonComp from "../../components/UI/atoms/button/Button";
import WidgetsComp from "../../components/UI/molecules/widget/Widget"
import { margin, padding } from "@mui/system";
import Text from "../../components/UI/atoms/text/Text";


// Utility: format currency
const formatCurrency = (amount) =>
  "₱" + (amount || 0).toLocaleString("en-PH", { minimumFractionDigits: 0 });

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PIE_COLORS = ["#C7A76C", "#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#a4de6c"];
const TOP_WIDGET_MIN_HEIGHT = 320;


const Analytics = () => {
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [pendingThirtyDayCount, setPendingThirtyDayCount] = useState(0);
  const [userDetails, setUserDetails] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [serviceTally, setServiceTally] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]); // For chart
  const [customDate, setCustomDate] = useState(null);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("")
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("")
  const [alertButtonType, setAlertButtonType] = useState("false")
  const [alertLink, setAlertLink] = useState("")

  // New states for new widgets
  const [patientRevenueDetails, setPatientRevenueDetails] = useState({});
  const [newPatients, setNewPatients] = useState(0);
  const [returningPatients, setReturningPatients] = useState(0);
  const [pieData, setPieData] = useState([
    { name: "Tuesday", value: 0 },
    { name: "Wednesday", value: 0 },
    { name: "Thursday", value: 0 },
    { name: "Friday", value: 0 },
    { name: "Saturday", value: 0 },
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();


  const userRoleFromHook = useUserRole();
  const userRole = location.state?.userRole || userRoleFromHook || "DentistOwner";

  const PERIOD_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "week", label: "1 Week" },
    { key: "month", label: "1 Month" },
    { key: "6months", label: "6 Months" },
    { key: "year", label: "1 Year" },
    { key: "all", label: "All Time" },
    { key: "custom", label: "Custom Date" },
  ];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Logout handler
  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate("/", { replace: true });
    });
  };

  useEffect(() => {
  // Fetch all completed appointments from appointments > [date] > [appointmentUID]
  const fetchCompletedAppointments = async () => {
    const appointmentsRef = collection(db, "appointments");
    const dateDocs = await getDocs(appointmentsRef);
    const allAppointments = [];
    const monthsSet = new Set();
    const dailyRevenueMap = {};
    const trend = [];

    for (const dateDoc of dateDocs.docs) {
      const dateKey = dateDoc.id; // e.g., "2025-08-01"
      // Each field in dateDoc.data() is an appointment object
      const appointmentsObj = dateDoc.data();
      for (const uid in appointmentsObj) {
        const appt = appointmentsObj[uid];
        if (appt.status === "Completed") {
          appt._dateKey = dateKey;
          allAppointments.push(appt);

          // For available months dropdown
          const [year, month] = dateKey.split("-");
          if (year && month) monthsSet.add(`${year}-${month}`);

          // Revenue trend
          if (!dailyRevenueMap[dateKey]) dailyRevenueMap[dateKey] = 0;
          if (appt.totalBill !== undefined) dailyRevenueMap[dateKey] += Number(appt.totalBill) || 0;
          else if (appt.bill !== undefined) dailyRevenueMap[dateKey] += Number(appt.bill) || 0;
          else if (appt.totalFee) dailyRevenueMap[dateKey] += Number(appt.totalFee) || 0;
        }
      }
    }

    // Build trend array
    for (const date in dailyRevenueMap) {
      trend.push({ date, revenue: dailyRevenueMap[date] });
    }

    setAllAppointments(allAppointments);
    setAvailableMonths(Array.from(monthsSet).sort((a, b) => b.localeCompare(a)));
    setRevenueTrend(trend.sort((a, b) => new Date(a.date) - new Date(b.date)));
    setPendingThirtyDayCount(0); // Only completed appointments, so pending count is 0
  };

  fetchCompletedAppointments(); 
}, []);

// Update today's appointments fetch
useEffect(() => {
  const fetchTodayAppointments = async () => {
    const now = new Date();
    const formattedToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .split('T')[0];

    const todayDocRef = doc(db, "appointments", formattedToday);
    const todayDocSnap = await getDoc(todayDocRef);

    const list = [];
    if (todayDocSnap.exists()) {
      const appointmentsObj = todayDocSnap.data();
      for (const uid in appointmentsObj) {
        const appt = appointmentsObj[uid];
        appt._dateKey = formattedToday;
        list.push(appt);
      }
    }
    setTodayAppointments(list);
  };

  fetchTodayAppointments();
}, []);

  // Fetch user details for sidebar profile (search all personnel types)
  useEffect(() => {
    const personnelTypes = ["DentistOwner", "AssociateDentist", "ClinicStaff"];
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        for (const type of personnelTypes) {
          const userRef = doc(db, "users", user.uid);
          const snapshot = await getDoc(userRef);
          if (snapshot.exists()) {
            setUserDetails(snapshot.data());
            break;
          }
        }
      }
    });
    return () => {
      authUnsubscribe();
    };
  }, [auth]);

  // Helper: Get last 4 Saturdays for the selected month
const getMonthSaturdays = (trend, selectedMonth) => {
  // selectedMonth format: "YYYY-MM"
  const saturdays = trend.filter(item => {
    const dateObj = new Date(item.date);
    const monthStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
    return dateObj.getDay() === 6 && monthStr === selectedMonth;
  });
  // Get last 4 (latest first)
  return saturdays.slice(-4).map(item => ({
    ...item,
    label: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }));
};

function getMonthWeeklyTrend(revenueTrend, year, month) {
  // Find all Saturdays in the current month
  const saturdays = [];
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === 6) { // Saturday
      saturdays.push(new Date(d));
    }
  }

  // For each Saturday, sum revenue from previous Monday to Saturday (inclusive)
  return saturdays.slice(-4).map(sat => {
    // Don't mutate sat, create new date objects
    const weekStart = new Date(sat.getFullYear(), sat.getMonth(), sat.getDate() - 6);
    const weekEnd = new Date(sat.getFullYear(), sat.getMonth(), sat.getDate());
    weekStart.setHours(0, 0, 0, 0);
    weekEnd.setHours(23, 59, 59, 999); // Include all of Saturday

    let sum = 0;
    revenueTrend.forEach(item => {
      const itemDate = new Date(item.date);
      itemDate.setHours(0, 0, 0, 0); // Ensure midnight
      if (itemDate >= weekStart && itemDate <= weekEnd) {
        sum += item.revenue;
      }
    });
    return {
      date: weekEnd.toISOString().split("T")[0],
      label: `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
      revenue: sum
    };
  });
}



const getSaturdaysInRange = (trend, startDate, endDate) => {
  return trend
    .filter(item => {
      const dateObj = new Date(item.date);
      return dateObj.getDay() === 6 && dateObj >= startDate && dateObj <= endDate;
    })
    .map(item => ({
      ...item,
      label: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      revenue: item.revenue
    }));
};

  // Helper: Get last 4 Saturdays from revenueTrend
const getLastFourSaturdays = (trend) => {
  const saturdays = [];
  for (let i = trend.length - 1; i >= 0 && saturdays.length < 4; i--) {
    const date = new Date(trend[i].date);
    if (date.getDay() === 6) { // Saturday
      saturdays.unshift(trend[i]);
    }
  }
  // If not enough, fill with most recent days
  if (saturdays.length < 4) {
    const needed = 4 - saturdays.length;
    const extras = trend.slice(-needed);
    saturdays.unshift(...extras);
  }
  return saturdays;
};

const saturdaysTrend = getLastFourSaturdays(revenueTrend).map(item => ({
  ...item,
  label: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}));

// Helper: Get last 7 days for "week"
const getWeekTrend = (trend) => {
  // Get today, set to midnight local time
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // Always start 6 days before today
  const weekDays = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
  
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    weekDays.push(`${year}-${month}-${day}`);
  }
  // Map trend data to these dates
  return weekDays.map(dateStr => {
    const found = trend.find(t => t.date === dateStr);
    return {
      date: dateStr,
      label: new Date(dateStr).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      revenue: found ? found.revenue : 0
    };
  });
};

const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  const cleaned = String(priceStr).replace(/,/g, "");
  const m = cleaned.match(/(\d+(\.\d+)?)/); // grab first number
  return m ? Number(m[0]) : 0;
};

const NORMALIZED_SERVICE_PRICE_MAP = Object.fromEntries(
  servicesList.map(s => [String(s.name).trim().toLowerCase(), parsePrice(s.price)])
);

// helper: returns canonical per-service price (appointment override > canonical map > equal split)
const resolveServicePrice = (serviceName, appt) => {
  const key = String(serviceName || "").trim();
  const normKey = key.toLowerCase();

  if (appt?.servicePrices) {
    if (appt.servicePrices[key] !== undefined) return Number(appt.servicePrices[key]) || 0;
    if (appt.servicePrices[normKey] !== undefined) return Number(appt.servicePrices[normKey]) || 0;
  }

  if (NORMALIZED_SERVICE_PRICE_MAP[normKey] !== undefined) return NORMALIZED_SERVICE_PRICE_MAP[normKey];

  // final fallback: split appointment total evenly
  const total = (() => {
    if (appt?.totalBill) return Number(appt.totalBill) || 0;
    if (appt?.bill) return Number(appt.bill) || 0;
    return Number(appt?.totalFee) || 0;
  })();

  const count = Array.isArray(appt.services) ? appt.services.length : (String(appt.services || "").split?.(",").length || 1);
  return count ? (total / count) : total;
};

function getAllTimeTrend(revenueTrend) {
  // Group by year and month
  const yearMonthMap = {};
  revenueTrend.forEach(item => {
    const dateObj = new Date(item.date);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    if (!yearMonthMap[year]) yearMonthMap[year] = {};
    if (!yearMonthMap[year][month]) yearMonthMap[year][month] = 0;
    yearMonthMap[year][month] += item.revenue;
  });

  const years = Object.keys(yearMonthMap);
  if (years.length === 1) {
    // Only one year, show by month
    const year = years[0];
    return Object.entries(yearMonthMap[year])
      .sort((a, b) => a[0] - b[0])
      .map(([month, revenue]) => ({
        label: new Date(Number(year), Number(month)).toLocaleString("default", { month: "short", year: "numeric" }),
        revenue
      }));
  } else {
    // Multiple years, show by year
    return years
      .sort((a, b) => a - b)
      .map(year => ({
        label: year,
        revenue: Object.values(yearMonthMap[year]).reduce((sum, rev) => sum + rev, 0)
      }));
  }
}


  // Centralized filtered appointments for all widgets
  const getFilteredAppointments = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (selectedPeriod === "week") {
    // Generate last 7 days ending today in YYYY-MM-DD format
    const weekDays = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      weekDays.push(`${year}-${month}-${day}`);
    }
    // Only include completed appointments for those 7 days
    return allAppointments.filter(
      (appt) => appt.status === "Completed" && weekDays.includes(appt._dateKey)
    );
  }

  if (selectedPeriod === "today") {
    return todayAppointments.filter((appt) => appt.status === "Completed");
  }

  if (selectedPeriod === "month") {
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);
    return allAppointments.filter((appt) => {
      if (appt.status === "Completed" && appt._dateKey) {
        const apptDate = new Date(appt._dateKey);
        return apptDate >= startDate && apptDate <= endDate;
      }
      return false;
    });
  }

  if (selectedPeriod === "6months") {
  const filtered = [];
  const nowMonth = now.getMonth();
  const nowYear = now.getFullYear();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(nowYear, nowMonth - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const startDate = new Date(year, month, 1);
    // For the current month, end on the last day of the month
    let endDate;
    if (year === nowYear && month === nowMonth) {
      endDate = new Date(year, month + 1, 0); // Last day of current month
    } else {
      endDate = new Date(year, month + 1, 0); // Last day of that month
    }
    endDate.setHours(23, 59, 59, 999);
    filtered.push(
      ...allAppointments.filter((appt) => {
        if (appt.status === "Completed" && appt._dateKey) {
          const apptDate = new Date(appt._dateKey);
          return apptDate >= startDate && apptDate <= endDate;
        }
        return false;
      })
    );
  }
  return filtered;
}

if (selectedPeriod === "year") {
  const filtered = [];
  const nowMonth = now.getMonth();
  const nowYear = now.getFullYear();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(nowYear, nowMonth - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const startDate = new Date(year, month, 1);
    // For each month, end on the last day of that month
    const endDate = new Date(year, month + 1, 0); // Last day of month
    endDate.setHours(23, 59, 59, 999);
    filtered.push(
      ...allAppointments.filter((appt) => {
        if (appt.status === "Completed" && appt._dateKey) {
          const apptDate = new Date(appt._dateKey);
          return apptDate >= startDate && apptDate <= endDate;
        }
        return false;
      })
    );
  }
  return filtered;
}

  if (selectedPeriod === "all") {
    return allAppointments.filter((appt) => appt.status === "Completed");
  }

  if (selectedPeriod === "custom" && customStartDate && customEndDate) {
    const start = new Date(customStartDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(customEndDate);
    end.setHours(23, 59, 59, 999);
    return allAppointments.filter((appt) => {
      if (appt.status === "Completed" && appt._dateKey) {
        const apptDate = new Date(appt._dateKey);
        return apptDate >= start && apptDate <= end;
      }
      return false;
    });
  }

  return [];
};

  const filteredAppointments = getFilteredAppointments();

// --- Revenue Calculation: Use bill/totalBill if present, fallback to totalFee ---
const getAppointmentRevenue = (appt) => {
  if (appt.totalBill !== undefined) return Number(appt.totalBill) || 0;
  if (appt.bill !== undefined) return Number(appt.bill) || 0;
  return Number(appt.totalFee) || 0;
};

// Total Revenue for selected period
const totalRevenue = filteredAppointments.reduce((sum, appt) => sum + getAppointmentRevenue(appt), 0);

const perPatientMap = {};
filteredAppointments.forEach(appt => {
  const pid = appt.patientId || appt.patientName || "unknown";
  if (!perPatientMap[pid]) perPatientMap[pid] = { count: 0, revenue: 0, name: appt.patientName || "Unknown" };
  perPatientMap[pid].count += 1;
  perPatientMap[pid].revenue += getAppointmentRevenue(appt);
});
let newPatientsRevenue = 0, returningPatientsRevenue = 0, newPatientsCount = 0, returningPatientsCount = 0;
Object.values(perPatientMap).forEach(p => {
  if (p.count === 1) {
    newPatientsRevenue += p.revenue;
    newPatientsCount += 1;
  } else {
    returningPatientsRevenue += p.revenue;
    returningPatientsCount += 1;
  }
});
const totalPatientRevenue = newPatientsRevenue + returningPatientsRevenue;
const newPatientPercent = totalPatientRevenue ? (newPatientsRevenue / totalPatientRevenue) * 100 : 0;
const returningPatientPercent = totalPatientRevenue ? (returningPatientsRevenue / totalPatientRevenue) * 100 : 0;

  // Patient Revenue Details
  const patientRevenue = {};
filteredAppointments.forEach((appt) => {
  if (appt.patientId) {
    if (!patientRevenue[appt.patientId]) {
      patientRevenue[appt.patientId] = { total: 0, count: 0, name: appt.patientName || "Unknown" };
    }
    patientRevenue[appt.patientId].total += getAppointmentRevenue(appt);
    patientRevenue[appt.patientId].count += 1;
  }
});

  // New vs Returning Patients
  let newCount = 0, returningCount = 0;
  
  // Count appointments per patient
  const patientAppointmentCounts = {};
  filteredAppointments.forEach((appt) => {
    if (appt.patientId) {
      patientAppointmentCounts[appt.patientId] = (patientAppointmentCounts[appt.patientId] || 0) + 1;
    }
  });
  
  // Determine new vs returning patients based on appointment count
  Object.values(patientAppointmentCounts).forEach(count => {
    if (count === 1) {
      newCount++;
    } else if (count >= 2) {
      returningCount++;
    }
  });

  // Pie Data: Daily Bookings
  const pie = { Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0, Saturday: 0 };
filteredAppointments.forEach((appt) => {
  if (appt._dateKey) {
    const dateObj = new Date(appt._dateKey);
    const day = dateObj.toLocaleDateString("en-US", { weekday: "long" });
    if (pie[day] !== undefined) pie[day]++;
  }
});
const pieDataPeriod = WEEKDAYS.map(day => ({ name: day, value: pie[day] }));

let serviceRevenuePeriod = [];
const revenueMap = {};
filteredAppointments.forEach(appt => {
  if (Array.isArray(appt.services)) {
    appt.services.forEach(service => {
      // Handle new format: service is an object with name and price
      let serviceName = "";
      let serviceRevenue = 0;
      
      if (typeof service === 'object' && service.name) {
        // New format: service object with name and price
        serviceName = service.name;
        serviceRevenue = Number(service.price) || 0;
      } else if (typeof service === 'string') {
        // Old format: service is a string
        serviceName = service;
        // Use individual service price if available, otherwise distribute total bill equally
        if (appt.servicePrices && appt.servicePrices[service]) {
          serviceRevenue = Number(appt.servicePrices[service]) || 0;
        } else {
          // Fallback: distribute total bill equally among services
          let totalRevenue = 0;
          if (appt.totalBill !== undefined) totalRevenue = Number(appt.totalBill) || 0;
          else if (appt.bill !== undefined) totalRevenue = Number(appt.bill) || 0;
          else totalRevenue = Number(appt.totalFee) || 0;
          
          serviceRevenue = totalRevenue / appt.services.length;
        }
      }
      
      if (serviceName) {
        revenueMap[serviceName] = (revenueMap[serviceName] || 0) + serviceRevenue;
      }
    });
  } else if (typeof appt.services === "string") {
    const servicesArray = appt.services.split(",").map(s => s.trim());
    servicesArray.forEach(service => {
      if (service) {
        let serviceRevenue = 0;
        if (appt.servicePrices && appt.servicePrices[service]) {
          serviceRevenue = Number(appt.servicePrices[service]) || 0;
        } else {
          // Fallback: distribute total bill equally among services
          let totalRevenue = 0;
          if (appt.totalBill !== undefined) totalRevenue = Number(appt.totalBill) || 0;
          else if (appt.bill !== undefined) totalRevenue = Number(appt.bill) || 0;
          else totalRevenue = Number(appt.totalFee) || 0;
          
          serviceRevenue = totalRevenue / servicesArray.length;
        }
        revenueMap[service] = (revenueMap[service] || 0) + serviceRevenue;
      }
    });
  }
});
// Convert to array and sort by revenue, take top 8
serviceRevenuePeriod = Object.entries(revenueMap)
  .map(([service, revenue]) => ({ service, revenue }))
  .sort((a, b) => b.revenue - a.revenue)
  .slice(0, 8);

  // Service tally for the selected period
  useEffect(() => {
    const tally = {};
    filteredAppointments.forEach((appt) => {
      if (Array.isArray(appt.services)) {
        appt.services.forEach((service) => {
          // Handle new format: service is an object with name and price
          let serviceName = "";
          if (typeof service === 'object' && service.name) {
            // New format: service object with name and price
            serviceName = service.name;
          } else if (typeof service === 'string') {
            // Old format: service is a string
            serviceName = service;
          }
          
          if (serviceName) {
            tally[serviceName] = (tally[serviceName] || 0) + 1;
          }
        });
      } else if (typeof appt.services === "string") {
        appt.services.split(",").map(s => s.trim()).forEach((service) => {
          if (service) tally[service] = (tally[service] || 0) + 1;
        });
      }
    });

    // compute period start/end for the selectedPeriod (local midnight)
    const now = new Date();
const toYMD = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()); // normalize to local midnight
let periodStart = null;
let periodEnd = null;
if (selectedPeriod === "today") {
  periodStart = toYMD(now);
  periodEnd = new Date(periodStart); periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "week") {
  periodEnd = toYMD(now);
  periodStart = new Date(periodEnd); periodStart.setDate(periodEnd.getDate() - 6);
  periodStart = toYMD(periodStart);
  periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "month") {
  periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "6months") {
  const startMonth = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  periodStart = toYMD(startMonth);
  periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "year") {
  const startMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  periodStart = toYMD(startMonth);
  periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "custom" && customStartDate && customEndDate) {
  periodStart = toYMD(new Date(customStartDate));
  periodEnd = new Date(customEndDate); periodEnd.setHours(23,59,59,999);
} else if (selectedPeriod === "all") {
  periodStart = null;
  periodEnd = null;
}

// build map of each patient's first-ever appointment date from allAppointments
const patientFirstApptDate = {};
allAppointments.forEach(a => {
  const pid = a.patientId || a.patientName || "unknown";
  if (!a._dateKey) return;
  const d = new Date(a._dateKey);
  if (!patientFirstApptDate[pid] || d < patientFirstApptDate[pid]) {
    patientFirstApptDate[pid] = d;
  }
});

let newPatientsRevenue = 0, returningPatientsRevenue = 0, newPatientsCount = 0, returningPatientsCount = 0;
Object.entries(perPatientMap).forEach(([pid, p]) => {
  const firstDate = patientFirstApptDate[pid];
  // If we have a first-ever date, patient is "new" only if that first-ever date is inside the current period.
  // If we don't have any history (missing from allAppointments), treat as new if they have appointments in filteredAppointments.
  const isNew = (() => {
    if (!firstDate) return true; // fallback: no history -> treat as new
    if (!periodStart || !periodEnd) return false; // 'all' -> not new (first-ever not "inside" an open range)
    const f = toYMD(firstDate);
    return f >= toYMD(periodStart) && f <= toYMD(periodEnd);
  })();

  if (isNew) {
    newPatientsRevenue += p.revenue;
    newPatientsCount += 1;
  } else {
    returningPatientsRevenue += p.revenue;
    returningPatientsCount += 1;
  }
});

    // evaluate unique patients in filteredAppointments and classify
    const uniquePatients = new Set();
    filteredAppointments.forEach(a => {
      const pid = a.patientId || a.patientName || "unknown";
      uniquePatients.add(pid);
    });

    let computedNew = 0;
    let computedReturning = 0;
    uniquePatients.forEach(pid => {
      const firstDate = patientFirstApptDate[pid];
      if (!firstDate) {
        // if we don't have history, treat as new (or adjust per preference)
        computedNew++;
      } else if (!periodStart || !periodEnd) {
        // 'all' case: consider first appointment within 'all' -> treat as new=false
        computedReturning++;
      } else {
        // check if first-ever date is inside the current period
        if (firstDate >= periodStart && firstDate <= periodEnd) computedNew++;
        else computedReturning++;
      }
    });

    // set states and other values
    setServiceTally(tally);
    setPatientRevenueDetails(patientRevenue);
    setNewPatients(computedNew);
    setReturningPatients(computedReturning);
    setPieData(pieDataPeriod);
  // eslint-disable-next-line
  }, [selectedPeriod, todayAppointments, allAppointments, customDate]);

  const serviceBreakdown = {};
filteredAppointments.forEach(appt => {
  if (Array.isArray(appt.services)) {
    appt.services.forEach(service => {
      // Handle new format: service is an object with name and price
      let serviceName = "";
      let servicePrice = 0;
      let serviceRevenue = 0;
      
      if (typeof service === 'object' && service.name) {
        // New format: service object with name and price - USE THE ACTUAL PRICE FROM THE OBJECT
        serviceName = String(service.name || service).trim();
        servicePrice = Number(service.price) || 0;
        serviceRevenue = servicePrice;
      } else if (typeof service === 'string') {
        // Old format: service is a string
        serviceName = service;
        // Use individual service price if available
        if (appt.servicePrices && appt.servicePrices[service]) {
          servicePrice = Number(appt.servicePrices[service]) || 0;
          serviceRevenue = servicePrice;
        } else {
          // Fallback: use average price from total bill
          let totalRevenue = getAppointmentRevenue(appt);
          servicePrice = totalRevenue / appt.services.length;
          serviceRevenue = servicePrice;
        }
      }
      
      if (serviceName) {
        if (!serviceBreakdown[serviceName]) {
          serviceBreakdown[serviceName] = { price: servicePrice, appointments: 0, revenue: 0 };
        }
        serviceBreakdown[serviceName].appointments += 1;
        serviceBreakdown[serviceName].revenue += serviceRevenue;
      }
    });
  } else if (typeof appt.services === "string") {
    const servicesArray = appt.services.split(",").map(s => s.trim());
    servicesArray.forEach(service => {
      if (service) {
        // Use individual service price if available
        let servicePrice = 0;
        let serviceRevenue = 0;
        
        if (appt.servicePrices && appt.servicePrices[service]) {
          servicePrice = Number(appt.servicePrices[service]) || 0;
          serviceRevenue = servicePrice;
        } else {
          // Fallback: use average price from total bill
          let totalRevenue = getAppointmentRevenue(appt);
          servicePrice = totalRevenue / servicesArray.length;
          serviceRevenue = servicePrice;
        }
        
        if (!serviceBreakdown[service]) {
          serviceBreakdown[service] = { price: servicePrice, appointments: 0, revenue: 0 };
        }
        serviceBreakdown[service].appointments += 1;
        serviceBreakdown[service].revenue += serviceRevenue;
      }
    });
  }
});

const totalRevenueForBreakdown = Object.values(serviceBreakdown).reduce((sum, s) => sum + s.revenue, 0);
const serviceBreakdownArray = Object.entries(serviceBreakdown)
  .map(([service, info]) => ({
    service,
    price: info.price,
    appointments: info.appointments,
    revenue: info.revenue,
    percent: totalRevenueForBreakdown ? (info.revenue / totalRevenueForBreakdown * 100) : 0
  }))
  .sort((a, b) => b.revenue - a.revenue);

  function getLastSixMonthsTrend(revenueTrend) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: d.getFullYear(),
      month: d.getMonth(),
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
      start: new Date(d.getFullYear(), d.getMonth(), 1),
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0)
    });
  }
  return months.map(({ label, start, end }) => {
    const sum = revenueTrend
      .filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= start && itemDate <= end;
      })
      .reduce((acc, item) => acc + item.revenue, 0);
    return { label, revenue: sum };
  });
}

function getLastTwelveMonthsTrend(revenueTrend) {
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: d.getFullYear(),
      month: d.getMonth(),
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
      start: new Date(d.getFullYear(), d.getMonth(), 1),
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0)
    });
  }
  return months.map(({ label, start, end }) => {
    const sum = revenueTrend
      .filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= start && itemDate <= end;
      })
      .reduce((acc, item) => acc + item.revenue, 0);
    return { label, revenue: sum };
  });
}




// Prepare chart data based on selectedPeriod
let chartData = [];
if (selectedPeriod === "week") {
  chartData = getWeekTrend(revenueTrend);
} else if (selectedPeriod === "month") {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  chartData = getMonthWeeklyTrend(revenueTrend, currentYear, currentMonth);
} else if (selectedPeriod === "6months") {
  chartData = getLastSixMonthsTrend(revenueTrend);
} else if (selectedPeriod === "year") {
  chartData = getLastTwelveMonthsTrend(revenueTrend);
} else if (selectedPeriod === "custom" && customStartDate && customEndDate) {
  chartData = getCustomTrend(filteredAppointments, customStartDate, customEndDate);
} else if (selectedPeriod === "all") {
  chartData = getAllTimeTrend(revenueTrend);
} else {
  chartData = getLastFourSaturdays(revenueTrend).map(item => ({
    ...item,
    label: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    revenue: item.revenue
  }));
}


const revenues = chartData.map(d => Number(d.revenue) || 0);
const maxRevenue = revenues.length ? Math.max(...revenues) : 0;
const minRevenue = revenues.length ? Math.min(...revenues) : 0;

let yDomain;
if (maxRevenue === 0) {
  yDomain = [0, 1];
} else {
  const range = maxRevenue - minRevenue;
  const pad = Math.max(maxRevenue * 0.10, range * 0.60);
  const minDomain = Math.max(0, maxRevenue - pad);
  const maxDomain = maxRevenue;
  yDomain = [minDomain, maxDomain];
}

// Helper for custom period: generate chart data for custom date range
function getCustomTrend(filteredAppointments, customStartDate, customEndDate) {
  if (!customStartDate || !customEndDate) return [];
  const start = new Date(customStartDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(customEndDate);
  end.setHours(0, 0, 0, 0);

  // Get all days in range, including start and end date
  const days = [];
  let d = new Date(start);
  while (d <= end) {
    // Format as YYYY-MM-DD to match _dateKey
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    days.push(dateStr);
    d.setDate(d.getDate() + 1);
  }

  // If less than or equal to 15 days, show daily
  if (days.length <= 15) {
    return days.map(dateStr => {
      // Sum revenue for this day from filteredAppointments
      const sum = filteredAppointments
        .filter(appt => appt._dateKey === dateStr)
        .reduce((acc, appt) => {
          if (appt.totalBill !== undefined) return acc + Number(appt.totalBill) || 0;
          if (appt.bill !== undefined) return acc + Number(appt.bill) || 0;
          return acc + (Number(appt.totalFee) || 0);
        }, 0);
      return {
        date: dateStr,
        label: new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
        revenue: sum
      };
    });
  } else {
    // More than 15 days: group by multiples (e.g. 7 days per group)
    const groupSize = Math.ceil(days.length / 7); // 7 groups max
    const groups = [];
    for (let i = 0; i < days.length; i += groupSize) {
      const groupDays = days.slice(i, i + groupSize);
      const startLabel = new Date(groupDays[0]).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      const endLabel = new Date(groupDays[groupDays.length - 1]).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      const label = `${startLabel} - ${endLabel}`;
      // Sum revenue for this group from filteredAppointments
      const sum = groupDays.reduce((acc, dateStr) => {
        return acc + filteredAppointments
          .filter(appt => appt._dateKey === dateStr)
          .reduce((a, appt) => {
            if (appt.totalBill !== undefined) return a + Number(appt.totalBill) || 0;
            if (appt.bill !== undefined) return a + Number(appt.bill) || 0;
            return a + (Number(appt.totalFee) || 0);
          }, 0);
      }, 0);
      groups.push({ label, revenue: sum });
    }
    return groups;
  }
}

const WrappedTick = ({ x, y, payload }) => {
  const maxChars = 18;     // characters per line
  const lineHeight = 12;   // px between wrapped lines
  const raw = String(payload?.value || "");
  // split on spaces and force-break long words
  const words = raw.split(/\s+/).flatMap(w => {
    if (w.length <= maxChars) return [w];
    const parts = [];
    for (let i = 0; i < w.length; i += maxChars) parts.push(w.slice(i, i + maxChars));
    return parts;
  });

  const lines = [];
  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length <= maxChars) {
      current = (current + " " + word).trim();
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  // Enforce max 2 lines: collapse remaining text into second line with ellipsis
  if (lines.length > 2) {
    const first = lines[0];
    const remainder = lines.slice(1).join(" ");
    let second = remainder;
    if (second.length > maxChars) second = second.slice(0, maxChars - 1).trim() + "…";
    lines.length = 0;
    lines.push(first, second);
  }

  // shift down so it doesn't collide with axis baseline
  const offsetY = 8;
  return (
    <g transform={`translate(${x},${y + offsetY})`}>
      <text x={0} y={0} textAnchor="middle" fontSize={11} fill="#666">
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      redirect && redirect();
      notify && notify();
    };
  }, []);

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  }
  
  return (
    <div>

      {/* All Content */}
      <Box 
        sx={boxStylesContent}>

        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

        {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}>

            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >

            {/* Main Content */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#FDF9F3",
                // backgroundColor: "#f8f5ef",
              }}>

              <Stack
                direction="column">

                <Header
                  page="Analytics"
                  count={1}/>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                      height: "calc(100vh - 110px)", // 👈 fills height below the header (adjust 80px if header height changes)
                      overflow: "auto",
                      mt: "0px",
                      // padding: "0px"
                    }}
                  >

        {/* Main Content Container with padding */}
                    <Box 
                    sx={{
                      padding: 0, 
                      flex: 1, background: "#FAF7F3"
                    }}>

                  {/* Period Selection */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>

                {/* Selects & Calendar & Summary*/}
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={0.5}
                  display={{
                    xs: "flex",
                    sm: "flex",
                    md: "flex",
                    lg: "flex",
                    xl: "flex",
                  }} 
                  sx={{
                    // mt: "40px",
                    padding: {
                      xs: "15px 15px 15px 15px",
                      sm: "15px 15px 15px 15px",
                      md: "15px 15px 15px 15px",
                      lg: "15px 15px 15px 15px",
                      xl: "15px 15px 15px 15px",
                    },
                    width: {
                      xs: "100%",
                      sm: "100%",
                      md: "100%",
                      lg: "70%",
                      xl: "60%",
                    },
                    borderRadius: "10px",
                    // backgroundColor: "rgba(46, 45, 45, .1)",
                    justifyContent: "center",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                  }}>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 1.8, 
                  md: 1.8, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  justifyContent: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "today" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "today" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "today" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("today")}>
                  Today
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 2, 
                  md: 2, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "week" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "week" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "week" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("week")}>
                  {isSmallScreen ? "1 Wk" : "1 Week"}
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 2, 
                  md: 2, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "month" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "month" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "month" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("month")}>
                  {isSmallScreen ? "1 Mth" : "1 Month"}
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 2.2, 
                  md: 2.2, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "6months" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "6months" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "6months" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("6months")}>
                  {isSmallScreen ? "6 Mos" : "6 Months"}
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 2, 
                  md: 2, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "year" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "year" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "year" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("year")}>
                  {isSmallScreen ? "1 Yr" : "1 Year"}
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: 2, 
                  sm: 2, 
                  md: 2, 
                  lg: 1.7, 
                  xl: 1.7
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "all" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "all" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "all" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("all")}>
                {isSmallScreen ? "All" : "All Time"}
                </Box>
              </Grid>

              <Grid
                size={{ 
                  xs: selectedPeriod === "custom" ? 4 : 12, 
                  sm: selectedPeriod === "custom" ? 4 : 12, 
                  md: selectedPeriod === "custom" ? 4 : 12, 
                  lg: 1.8, 
                  xl: 1.8
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  color: selectedPeriod === "custom" ? "#FEFEFE" : "#2C251E",
                  border: "1px solid rgba(89, 84, 74, .15)",
                  backgroundColor: selectedPeriod === "custom" ? "#C5A572" : "#FEFEFE",
                  cursor: "pointer",
                  ":hover": {
                    backgroundColor: selectedPeriod === "custom" ? "#C5A572" : "rgba(197, 165, 114, .3)",
                  }
                }}
              >
                <Box
                  onClick={() => setSelectedPeriod("custom")}>
                {isSmallScreen ? "Custom" : "Custom"}
                </Box>
              </Grid>

              <Grid
                display={{
                  xs : selectedPeriod === "custom" ? "flex" : "none",
                  sm : selectedPeriod === "custom" ? "flex" : "none",
                  md : selectedPeriod === "custom" ? "flex" : "none",
                  lg : "none",
                  xl : "none",
                }}
                size={{ 
                  xs: selectedPeriod === "custom" ? 8 : 12, 
                  sm: selectedPeriod === "custom" ? 8 : 12, 
                  md: selectedPeriod === "custom" ? 8 : 12, 
                  lg: 1.8, 
                  xl: 1.8
                }} 
                item
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  textAlign: "center",
                  padding: "10px 0",
                  fontSize: "14px",
                  borderRadius: "10px",
                  justifyContent: "center"
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}>

                  <Stack
                    direction="row"
                    spacing={1}>
                      <Text
                        text="Start:"
                        variant="testimonialsH3"/>
                    <DatePicker
                      selected={customStartDate}
                      onChange={date => setCustomStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Start date"
                      maxDate={customEndDate || new Date()}
                      isClearable
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      style={{ marginLeft: 8 }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}>
                      <Text
                        text="End:"
                        variant="testimonialsH3"/>
              <DatePicker
                selected={customEndDate}
                onChange={date => setCustomEndDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="End date"
                minDate={customStartDate}
                maxDate={new Date()}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                style={{ marginLeft: 8 }}
              />
                  </Stack>
                </Stack>
              </Grid>

              </Grid>

                    {/* Show calendar if custom is selected */}
                    {selectedPeriod === "custom" && (
          <div style={{ marginLeft: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <span>Start:</span>
              <DatePicker
                selected={customStartDate}
                onChange={date => setCustomStartDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Start date"
                maxDate={customEndDate || new Date()}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                style={{ marginLeft: 8 }}
              />
            </div>
            <div>
              <span>End:</span>
              <DatePicker
                selected={customEndDate}
                onChange={date => setCustomEndDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="End date"
                minDate={customStartDate}
                maxDate={new Date()}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                style={{ marginLeft: 8 }}
              />
            </div>
          </div>
        )}


                  </div>

                  <Divider
                    sx={{
                      margin: isSmallScreen ? "0" : "0 20px",
                      backgroundColor: "rgba(89, 84, 74, 1)"
                    }}/>

                <Box
                  sx={{
                      maxHeight: "calc(80vh - 10px)",
                      overflow: "auto",
                  }}>
                 {/* Top Widgets */}
               <Grid
                    container
                    spacing={{
                        xs: 3,
                        sm: 3,
                        md: 3,
                        lg: 3,
                        xl: 3,
                    }}
                    alignItems="stretch"
                    sx={{ 
                      width: "100%", my: "20px", px: "20px"
                      }}
                  >
                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 4,
                      }}>
                        <Stack
                          spacing={2}
                          direction={{
                            xs: "column",
                            sm: "column",
                            md: "column",
                            lg: "row",
                            xl: "column",
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ width: "100%" }}>
                            <WidgetsComp
                              src={require("../../assets/calendar.png")}
                              label="Total Appointments"
                              defaults={true}
                              content={filteredAppointments.length}
                              tip="Number of appointments booked."
                            />
                          </Box>

                          <Box sx={{ width: "100%" }}>
                            <WidgetsComp
                              src={require("../../assets/icons/money.png")}
                              label="Total Revenue"
                              defaults={true}
                              content={formatCurrency(totalRevenue)}
                              tip="Total income generated from all services within the given period." 
                            />
                          </Box>
                        </Stack>
                    </Grid>

                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 6,
                        xl: 4,
                      }}>
                        <WidgetsComp
                          src={require("../../assets/icons/patients.png")}
                          label="Patient Revenue Details"
                          defaults={true}
                          fullHeight={true}
                          tip="Breaks down earnings from new vs. returning patients."
                          content={
                            <Box sx={{ width: "100%", pt: 2, display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                              {/* existing content (new/returning, progress bar) */}
                              <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start", mb: 3, justifyContent: "center" }}>
                                <Box sx={{ minWidth: 0, textAlign: "center" }}>
                                  <Typography sx={{ fontSize: 15, color: "#888", mb: 1 }}>New Patients</Typography>
                                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#23201A", mb: 0.5 }}>
                                    {formatCurrency(Math.round(newPatientsRevenue))}
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "#666" }}>{newPatientsCount} patients</Typography>
                                </Box>
                                <Box sx={{ minWidth: 0, textAlign: "center" }}>
                                  <Typography sx={{ fontSize: 15, color: "#888", mb: 1 }}>Returning Patients</Typography>
                                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#23201A", mb: 0.5 }}>
                                    {formatCurrency(Math.round(returningPatientsRevenue))}
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "#666" }}>{returningPatientsCount} patients</Typography>
                                </Box>
                              </Box>

                              {/* two-segment percentage bar: new patients (left) vs returning (right) */}
                                <Box sx={{ height: "30px", bgcolor: "#F3EEE8", borderRadius: 10, overflow: "hidden", display: "flex", mb: 2 }}>
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: `${newPatientPercent}%`,
                                      bgcolor: "#C7A76C",
                                      transition: "width 240ms ease",
                                      minWidth: newPatientPercent > 0 && newPatientPercent < 2 ? 8 : 0,
                                      borderTopLeftRadius: 10,
                                      borderBottomLeftRadius: 10,
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: `${returningPatientPercent}%`,
                                      bgcolor: "#E6DFD6",
                                      transition: "width 240ms ease",
                                      minWidth: returningPatientPercent > 0 && returningPatientPercent < 2 ? 8 : 0,
                                      borderTopRightRadius: 10,
                                      borderBottomRightRadius: 10,
                                    }}
                                  />
                                </Box>

                                {/* labels with revenue and percent for each side */}
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18 }}>
                                  <Box>
                                    <Typography sx={{ fontSize: 14, color: "#666", mb: 0.5 }}>New Patients</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#23201A" }}>
                                      {newPatientPercent.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: "right" }}>
                                    <Typography sx={{ fontSize: 14, color: "#666", mb: 0.5 }}>Returning Patients</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#23201A" }}>
                                      {returningPatientPercent.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>     
                          }
                        />
                    </Grid>

                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 6,
                        xl: 4,
                      }}>
                        <WidgetsComp
                          src={require("../../assets/calendar.png")}
                          label="Daily Patient Bookings (Mon-Sat)"
                          defaults={true}
                          fullHeight={true}
                          tip="Highlights which days see the most patient bookings."
                          content={
                            <Box sx={{ width: "100%", display: "flex", gap: 3, alignItems: "center", pt: 1, flex: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ResponsiveContainer width="100%" height={220}>
                                  <PieChart>
                                    <Pie
                                      data={pieDataPeriod}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius="45%"
                                      outerRadius="75%"
                                      paddingAngle={2}
                                      dataKey="value"
                                    >
                                      {pieDataPeriod.map((entry, idx) => (
                                        <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip />
                                  </PieChart>
                                </ResponsiveContainer>
                              </Box>

                              <Box sx={{ width: 140, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                                {pieDataPeriod.map((item, idx) => (
                                  <Box key={item.name} sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                                    <Box sx={{ width: 16, height: 16, bgcolor: PIE_COLORS[idx % PIE_COLORS.length], mr: 1.5, borderRadius: 1, border: "1px solid #ddd" }} />
                                    <Typography sx={{ fontSize: 14, color: "#23201A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          }
                        />
                    </Grid>
                  </Grid>

                    {/* Revenue Trend */}
             {selectedPeriod !== "today" && (
               <Grid
                    container
                    spacing={{
                        xs: 3,
                        sm: 3,
                        md: 3,
                        lg: 3,
                        xl: 3,
                    }}
                    alignItems="stretch"
                    sx={{ 
                      width: "100%", 
                      my: "20px",
                      px: "20px"
                    }}
                  >

                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                      }}>
                        <WidgetsComp
                          src={require("../../assets/icons/revenue_trend.png")}
                          label="Revenue Trend"
                          defaults={true}
                          fullHeight={true}
                          tip="Highlights which days see the most patient bookings."
                          content={
          <div style={{
            // background: "#fb1414ff",
            margin: "20px 0",
          }}>
           <ResponsiveContainer width="100%" height={220}>
  <LineChart
    data={chartData}
    // margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
    margin={{ 
      right: 10,
      top: 10, left: 10 }}
  >
    <CartesianGrid stroke="rgba(89, 84, 74, .2)" strokeDasharray="3 3" />
    <XAxis dataKey="label" tick={{ fontSize: "17px", dy: 8, fill: "#2C251E" }}/>
    {isSmallScreen ? null : (
<YAxis
      domain={yDomain}                  // <-- apply computed domain
      tickFormatter={value => formatCurrency(value)}
      tick={{ fontSize: "17px", dx: "30px", fill: "#2C251E" }}
      width={80}
    />
    )}
    
    <Tooltip formatter={value => formatCurrency(value)} />
    <Line
      type="monotone"
      dataKey="revenue"
      stroke="#C5A572"
      strokeWidth={3}
      dot={{ r: 7, stroke: "#C5A572", strokeWidth: 2, fill: "#fff" }}
      activeDot={{ r: 9, fill: "#C5A572" }}
    />
  </LineChart>
</ResponsiveContainer>
          </div>
                          }
                        />
                    </Grid>
                  </Grid>
        )}

          
                    {/* Service Revenue */}
          {serviceRevenuePeriod.length > 0 && (
               <Grid
                    container
                    spacing={{
                        xs: 3,
                        sm: 3,
                        md: 3,
                        lg: 3,
                        xl: 3,
                    }}
                    alignItems="stretch"
                    sx={{ 
                      width: "100%", 
                      my: "20px",
                      px: "20px"
                    }}
                  >

                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                      }}>
                        <WidgetsComp
                          src={require("../../assets/services_surgery.png")}
                          label="Service Revenue"
                          defaults={true}
                          fullHeight={true}
                          tip="Highlights which days see the most patient bookings."
                          content={
          <div style={{
            // background: "#fb1414ff",
            margin: "20px 0",
          }}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={serviceRevenuePeriod}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="service" interval={0} tick={<WrappedTick />} />
                <YAxis tickFormatter={value => formatCurrency(value)} tick={{ fontSize: 13 }} />
                <Tooltip formatter={value => formatCurrency(value)} />
                <Bar dataKey="revenue" fill="#C7A76C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
                          }
                        />
                    </Grid>
                  </Grid>
        )}
        
               <Grid
                    container
                    spacing={{
                        xs: 3,
                        sm: 3,
                        md: 3,
                        lg: 3,
                        xl: 3,
                    }}
                    alignItems="stretch"
                    sx={{ 
                      width: "100%", 
                      my: "20px",
                      px: "20px"
                    }}
                  >

                    <Grid 
                      item 
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                      }}>
                        <WidgetsComp
                          src={require("../../assets/icons/revenue_trend.png")}
                          label="Revenue Breakdown"
                          defaults={true}
                          fullHeight={true}
                          tip="Highlights which days see the most patient bookings."
                          content={
                  <div style={{ 
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          boxShadow: "0 2px 8px #00000010"
        }}>
          <h3>
          (
          {selectedPeriod === "today"
            ? "Today"
            : selectedPeriod === "week"
            ? "Last 7 Days"
            : selectedPeriod === "month"
            ? "Previous Month"
            : selectedPeriod === "6months"
            ? "Past 6 Months"
            : selectedPeriod === "year"
            ? "1 Year"
            : selectedPeriod === "all"
            ? "All Time"
            : selectedPeriod === "custom" && customStartDate && customEndDate
            ? `${customStartDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })} - ${customEndDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`
            : ""}
          )
        </h3>
          {serviceBreakdownArray.length === 0 ? (
            <p>No completed appointments for this period.</p>
          ) : (
                      <TableContainer
                        component={Paper}
                          sx={{
                            // borderRadius: "10px",
                            boxShadow: "0px 2px 8px .5px #59544A",
                            maxHeight: "650px",
                            minHeight:"650px",
                            // padding: "20px",
                            marginTop: "20px",
                            borderRadius: "20px",
                            // backgroundColor: "#C5A572",
                        }}>
                        <Table
                          aria-label="simple table"
                          stickyHeader
                          stickyFooter
                          sx={{
                            padding: "0 20px 0 20px",
                            tableLayout: "fixed",
                            width: "100%",
                          }}
                        >
                          {/* Table Head */}
                          <TableHead sx={{
                              backgroundColor: "#2E2D2D"
                              }}>
                            <TableRow
                            sx={{
                              backgroundColor: "#2E2D2D"
                            }}>
                              <TableCell
                                sx={{
                                 borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Treatment Service
                              </TableCell>

                              <TableCell
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Price
                              </TableCell>


                              <TableCell
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Service Count
                              </TableCell>

                              <TableCell
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                Total Revenue
                              </TableCell>


                              <TableCell
                                sx={{
                                  borderBottom : "2px solid rgba(89, 84, 74, 1)",
                                  color: "rgba(44, 37, 30, .8)",
                                  padding: "30px 10px 20px 10px",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  gap: 1
                                }}
                              >
                                % of Revenue
                              </TableCell>
                            </TableRow>
                          </TableHead>
              <tbody>
          {serviceBreakdownArray.map(row => (
            <tr key={row.service}>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>{row.service}</td>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>{formatCurrency(row.price)}</td>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>{row.appointments}</td>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>{formatCurrency(row.revenue)}</td>
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>{row.percent.toFixed(1)}%</td>
            </tr>
          ))}
          {/* Total Row */}
          <tr style={{ fontWeight: "bold", background: "rgba(197, 165, 114, .2)" }}>
            <td style={{ border: "1px solid #ccc", padding: "8px" }}>Total</td>
            <td style={{ border: "1px solid #ccc", padding: "8px" }}></td>
            <td style={{ border: "1px solid #ccc", padding: "8px" }}>
              {serviceBreakdownArray.reduce((sum, row) => sum + row.appointments, 0)}
            </td>
            <td style={{ border: "1px solid #ccc", padding: "8px" }}>
              {formatCurrency(totalRevenue)}
            </td>
            <td style={{ border: "1px solid #ccc", padding: "8px" }}>100%</td>
          </tr>
        </tbody>
            </Table>
            </TableContainer>
          )}
        </div>
                          }
                        />
                    </Grid>
                  </Grid>
</Box>
                
                </Box>
            </Box>
              </Stack>

            </Box>

          </Grid>

        </Grid>

      </Box>

    <SplashScreen
      isOpen={showCustomAlert}
      image={alertImage}
      button={alertButton}
      buttonType={alertButtonType}
      destination={alertLink}
      onClose={() => setShowCustomAlert(false)}/>

    </div>
  );
};

export default Analytics;